import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaChevronRight,
  FaComments,
  FaEdit,
  FaFilter,
  FaPlus,
  FaSearch,
  FaTasks,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import DevActivityService from "../service/DevActivityService";
import {
  AREAS,
  CREATE_STATUSES,
  getTodayDateString,
  PRIORITIES,
  priorityBadgeStyle,
  STATUSES,
  validateActivityDates,
} from "../utils/devActivityValidation";

const emptyActivity = () => ({
  id: null,
  title: "",
  module: "Student Management",
  area: "Frontend + Backend",
  description: "",
  status: "not_started",
  priority: "medium",
  plannedStart: "",
  plannedEnd: "",
  actualStart: "",
  actualEnd: "",
});

const issueKey = (task, index = 0) => `DEV-${String(task.id || index + 1).padStart(3, "0")}`;

const statusStyle = (status, dark) =>
  ({
    completed: dark ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-800",
    in_progress: dark ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-800",
    not_started: dark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700",
  }[status]);

export default function DevActivityTracker({ isDarkMode = false }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const shell = isDarkMode ? "border-slate-800 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900";
  const surface = isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-slate-50";
  const field = isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-white text-slate-900";

  useEffect(() => {
    let active = true;
    DevActivityService.getActivities()
      .then((items) => {
        if (active) {
          setTasks(
            items.map((task) => ({
              ...emptyActivity(),
              ...task,
              priority: task.priority || "medium",
            }))
          );
        }
      })
      .catch((error) => toast.error(error.message || "Unable to load development activities."))
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const modules = useMemo(() => [...new Set(tasks.map((task) => task.module).filter(Boolean))], [tasks]);
  const counts = useMemo(
    () => ({
      all: tasks.length,
      not_started: tasks.filter((x) => x.status === "not_started").length,
      in_progress: tasks.filter((x) => x.status === "in_progress").length,
      completed: tasks.filter((x) => x.status === "completed").length,
    }),
    [tasks]
  );

  const shown = useMemo(
    () =>
      tasks.filter((task) => {
        const needle = query.trim().toLowerCase();
        const textMatch =
          !needle ||
          `${task.title} ${task.module} ${task.description} ${task.id} ${task.priority || ""}`
            .toLowerCase()
            .includes(needle);
        return (
          textMatch &&
          (statusFilter === "all" || task.status === statusFilter) &&
          (moduleFilter === "all" || task.module === moduleFilter) &&
          (priorityFilter === "all" || (task.priority || "medium") === priorityFilter)
        );
      }),
    [tasks, query, statusFilter, moduleFilter, priorityFilter]
  );

  const selected = tasks.find((task) => task.id === selectedId) || null;

  const updateStatus = async (task, status) => {
    if (task.status === status) return;
    const payload = { status };
    const today = getTodayDateString();
    if (status === "in_progress" && !task.actualStart) payload.actualStart = today;
    if (status === "completed" && !task.actualEnd) payload.actualEnd = today;

    const dateError = validateActivityDates({ ...task, ...payload }, false, task);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    const previous = tasks;
    setTasks((current) => current.map((item) => (item.id === task.id ? { ...item, ...payload } : item)));
    const toastId = toast.loading("Updating status...");
    try {
      await DevActivityService.updateActivity(task.id, payload);
      toast.update(toastId, { render: "Status updated.", type: "success", isLoading: false, autoClose: 2200 });
    } catch (error) {
      setTasks(previous);
      toast.update(toastId, {
        render: error.message || "Unable to update status.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const closeDrawer = () => {
    setEditor(null);
    setSelectedId(null);
  };
  const openDetails = (task) => navigate(`/super-admin/dev-activities/${task.id}`);
  const openEdit = (task) => {
    setSelectedId(task.id);
    setEditor({ ...emptyActivity(), ...task, priority: task.priority || "medium" });
  };
  const openCreate = () => navigate("/super-admin/dev-activities/new");

  const saveActivity = async (event) => {
    event.preventDefault();
    if (!editor?.title.trim() || saving) return;

    const isCreating = !editor.id;
    const original = isCreating ? {} : tasks.find((t) => t.id === editor.id) || {};
    const dateError = validateActivityDates(editor, isCreating, original);
    if (dateError) {
      toast.error(dateError);
      return;
    }

    setSaving(true);
    const toastId = toast.loading(isCreating ? "Creating activity..." : "Saving changes...");
    try {
      const payload = {
        ...editor,
        priority: editor.priority || "medium",
      };
      if (isCreating) delete payload.actualEnd;

      const saved = isCreating
        ? await DevActivityService.createActivity(payload)
        : await DevActivityService.updateActivity(editor.id, payload);
      const item = { ...emptyActivity(), ...editor, ...saved, id: saved?.id || editor.id };
      setTasks((current) =>
        isCreating ? [item, ...current] : current.map((task) => (task.id === editor.id ? item : task))
      );
      setEditor(null);
      setSelectedId(item.id);
      toast.update(toastId, {
        render: isCreating ? "Activity created." : "Activity updated.",
        type: "success",
        isLoading: false,
        autoClose: 2500,
      });
    } catch (error) {
      toast.update(toastId, {
        render: error.message || "Unable to save activity.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    } finally {
      setSaving(false);
    }
  };

  const deleteActivity = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    const previous = tasks;
    setDeleteTarget(null);
    setTasks((current) => current.filter((task) => task.id !== target.id));
    if (selectedId === target.id) closeDrawer();
    const toastId = toast.loading("Deleting activity...");
    try {
      await DevActivityService.deleteActivity(target.id);
      toast.update(toastId, { render: "Activity deleted.", type: "success", isLoading: false, autoClose: 2500 });
    } catch (error) {
      setTasks(previous);
      toast.update(toastId, {
        render: error.message || "Unable to delete activity.",
        type: "error",
        isLoading: false,
        autoClose: 3500,
      });
    }
  };

  const Drawer = () => {
    const activity = editor || selected;
    if (!activity) return null;
    const editing = Boolean(editor);
    const isCreate = editing && !editor.id;
    const today = getTodayDateString();
    const availableStatuses = isCreate ? CREATE_STATUSES : STATUSES;

    const setValue = (key, value) => {
      setEditor((current) => {
        const next = { ...current, [key]: value };
        if (key === "status" && value === "completed" && !next.actualEnd) {
          next.actualEnd = today;
        }
        if (key === "status" && value === "in_progress" && !next.actualStart) {
          next.actualStart = today;
        }
        return next;
      });
    };

    return (
      <div
        className="fixed inset-0 z-[70] flex justify-end bg-slate-950/35 backdrop-blur-[1px]"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeDrawer();
        }}
      >
        <aside
          className={`h-full w-full max-w-xl overflow-y-auto border-l shadow-2xl ${
            isDarkMode ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-white text-slate-900"
          }`}
        >
          <header
            className={`sticky top-0 z-10 flex items-center justify-between border-b px-5 py-4 ${
              isDarkMode ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
            }`}
          >
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-blue-600">
                {activity.id ? issueKey(activity) : "New activity"}
              </p>
              <h3 className="mt-1 font-bold">
                {editing ? (activity.id ? "Edit activity" : "Create activity") : "Activity details"}
              </h3>
            </div>
            <button
              type="button"
              onClick={closeDrawer}
              className="rounded-lg p-2 text-slate-500 hover:bg-slate-100"
              aria-label="Close details"
            >
              <FaTimes />
            </button>
          </header>
          {editing ? (
            <form onSubmit={saveActivity} className="space-y-5 p-5 sm:p-6">
              <label className="block text-sm font-semibold">
                Summary <span className="text-rose-500">*</span>
                <input
                  required
                  autoFocus
                  value={editor.title}
                  onChange={(e) => setValue("title", e.target.value)}
                  className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 ${field}`}
                />
              </label>
              <label className="block text-sm font-semibold">
                Description
                <textarea
                  value={editor.description}
                  onChange={(e) => setValue("description", e.target.value)}
                  rows={5}
                  className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm outline-none focus:border-blue-600 ${field}`}
                />
              </label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="text-sm font-semibold">
                  Module
                  <input
                    value={editor.module}
                    onChange={(e) => setValue("module", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Area
                  <select
                    value={editor.area}
                    onChange={(e) => setValue("area", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  >
                    {AREAS.map((area) => (
                      <option key={area}>{area}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold">
                  Priority
                  <select
                    value={editor.priority || "medium"}
                    onChange={(e) => setValue("priority", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  >
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold">
                  Status
                  <select
                    value={editor.status}
                    onChange={(e) => setValue("status", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  >
                    {availableStatuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="text-sm font-semibold">
                  Planned start
                  <input
                    type="date"
                    min={isCreate ? today : undefined}
                    value={editor.plannedStart || ""}
                    onChange={(e) => setValue("plannedStart", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Due date
                  <input
                    type="date"
                    min={isCreate ? (editor.plannedStart || today) : undefined}
                    value={editor.plannedEnd || ""}
                    onChange={(e) => setValue("plannedEnd", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  />
                </label>
                <label className="text-sm font-semibold">
                  Actual start
                  <input
                    type="date"
                    min={isCreate ? today : undefined}
                    value={editor.actualStart || ""}
                    onChange={(e) => setValue("actualStart", e.target.value)}
                    className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                  />
                </label>
                {!isCreate && (
                  <label className="text-sm font-semibold">
                    Completed date {editor.status === "completed" && <span className="text-rose-500">*</span>}
                    <input
                      type="date"
                      max={today}
                      required={editor.status === "completed"}
                      value={editor.actualEnd || ""}
                      onChange={(e) => setValue("actualEnd", e.target.value)}
                      className={`mt-2 w-full rounded-lg border px-3 py-2.5 text-sm ${field}`}
                    />
                  </label>
                )}
              </div>
              <div className="flex justify-end gap-3 border-t border-slate-200 pt-5">
                <button
                  type="button"
                  onClick={() => (editor.id ? setEditor(null) : closeDrawer())}
                  className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  disabled={saving}
                  className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-800 disabled:opacity-60"
                >
                  {saving ? "Saving..." : "Save activity"}
                </button>
              </div>
            </form>
          ) : (
            <div className="p-5 sm:p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-bold uppercase tracking-wider ${priorityBadgeStyle(
                        selected.priority,
                        isDarkMode
                      )}`}
                    >
                      {selected.priority || "medium"}
                    </span>
                  </div>
                  <h2 className="mt-2 text-2xl font-bold leading-8">{selected.title}</h2>
                </div>
                <button
                  type="button"
                  onClick={() => openEdit(selected)}
                  className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
                >
                  <FaEdit /> Edit
                </button>
              </div>
              <p className="mt-5 whitespace-pre-wrap text-sm leading-7 text-slate-600 dark:text-slate-300">
                {selected.description || "No description provided."}
              </p>
              <dl className={`mt-7 divide-y divide-slate-200 rounded-xl border ${surface}`}>
                {[
                  ["Status", STATUSES.find((x) => x.value === selected.status)?.label || selected.status],
                  ["Priority", (selected.priority || "medium").toUpperCase()],
                  ["Module", selected.module],
                  ["Area", selected.area],
                  ["Planned start", selected.plannedStart],
                  ["Due date", selected.plannedEnd],
                  ["Actual start", selected.actualStart],
                  ["Completed", selected.actualEnd],
                ].map(([label, value]) => (
                  <div key={label} className="grid grid-cols-[9rem_1fr] gap-3 px-4 py-3 text-sm">
                    <dt className="font-medium text-slate-500">{label}</dt>
                    <dd className="font-semibold">{value || "Not set"}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => openDetails(selected)}
                  className="inline-flex items-center gap-2 rounded-lg bg-blue-50 px-3.5 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
                >
                  <FaComments /> Comments & Full Details
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(selected)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-rose-600 hover:text-rose-700"
                >
                  <FaTrash /> Delete activity
                </button>
              </div>
            </div>
          )}
        </aside>
      </div>
    );
  };

  return (
    <section className={`overflow-hidden rounded-2xl border shadow-sm ${shell}`}>
      <header className="border-b border-slate-200 px-5 py-5 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-blue-600">
              <FaTasks /> Project workspace
            </p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight">Development activities</h1>
          </div>
          <button
            type="button"
            onClick={openCreate}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-blue-800"
          >
            <FaPlus /> Create activity
          </button>
        </div>
        <nav className="mt-5 flex gap-1 overflow-x-auto" aria-label="Status filters">
          {[{ value: "all", label: "All" }, ...STATUSES].map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setStatusFilter(item.value)}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold ${
                statusFilter === item.value ? "bg-blue-50 text-blue-700" : "text-slate-500 hover:bg-slate-100"
              }`}
            >
              {item.label} <span className="ml-1 text-xs">{counts[item.value]}</span>
            </button>
          ))}
        </nav>
      </header>
      <div className={`flex flex-col gap-3 border-b p-4 sm:flex-row ${surface}`}>
        <label className="relative min-w-0 flex-1">
          <FaSearch className="absolute left-3 top-3 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className={`w-full rounded-lg border py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-600 ${field}`}
            placeholder="Search activities by key, title, module, priority..."
          />
        </label>
        <label className="relative">
          <FaFilter className="pointer-events-none absolute left-3 top-3 text-slate-400" />
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className={`w-full rounded-lg border py-2.5 pl-9 pr-8 text-sm font-medium sm:w-44 ${field}`}
          >
            <option value="all">All priorities</option>
            {PRIORITIES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
        <label className="relative">
          <FaFilter className="pointer-events-none absolute left-3 top-3 text-slate-400" />
          <select
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
            className={`w-full rounded-lg border py-2.5 pl-9 pr-8 text-sm font-medium sm:w-52 ${field}`}
          >
            <option value="all">All modules</option>
            {modules.map((module) => (
              <option key={module}>{module}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[950px] table-fixed text-left text-sm">
          <thead className={isDarkMode ? "bg-slate-900 text-slate-400" : "bg-slate-50 text-slate-500"}>
            <tr>
              <th className="w-24 px-4 py-3 text-xs font-bold uppercase">Key</th>
              <th className="px-4 py-3 text-xs font-bold uppercase">Summary</th>
              <th className="w-28 px-4 py-3 text-xs font-bold uppercase">Priority</th>
              <th className="w-40 px-4 py-3 text-xs font-bold uppercase">Status</th>
              <th className="w-40 px-4 py-3 text-xs font-bold uppercase">Area</th>
              <th className="w-36 px-4 py-3 text-xs font-bold uppercase">Due date</th>
              <th className="w-16 px-4 py-3">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
            {loading && (
              <tr>
                <td colSpan="7" className="px-5 py-14 text-center text-slate-500">
                  Loading activities...
                </td>
              </tr>
            )}
            {!loading &&
              shown.map((task, index) => (
                <tr
                  key={task.id}
                  onClick={() => openDetails(task)}
                  className={isDarkMode ? "cursor-pointer hover:bg-slate-900" : "cursor-pointer hover:bg-blue-50/50"}
                >
                  <td className="px-4 py-3 font-semibold text-blue-700">{issueKey(task, index)}</td>
                  <td className="px-4 py-3">
                    <p className="truncate font-semibold">{task.title}</p>
                    <p className="mt-1 truncate text-xs text-slate-500">{task.module}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-bold capitalize ${priorityBadgeStyle(
                        task.priority,
                        isDarkMode
                      )}`}
                    >
                      {task.priority || "medium"}
                    </span>
                  </td>
                  <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                    <select
                      value={task.status}
                      onChange={(e) => updateStatus(task, e.target.value)}
                      className={`w-full rounded-md border-0 px-2.5 py-2 text-xs font-bold outline-none ${statusStyle(
                        task.status,
                        isDarkMode
                      )}`}
                    >
                      {STATUSES.map((status) => (
                        <option key={status.value} value={status.value}>
                          {status.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{task.area}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-300">
                      <FaCalendarAlt className="text-slate-400" /> {task.plannedEnd || "Not set"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right text-slate-400">
                    <FaChevronRight />
                  </td>
                </tr>
              ))}
            {!loading && shown.length === 0 && (
              <tr>
                <td colSpan="7" className="px-5 py-14 text-center text-slate-500">
                  No activities match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <footer className={`border-t px-5 py-3 text-xs text-slate-500 ${surface}`}>
        {shown.length} of {tasks.length} activities
      </footer>
      <Drawer />
      {deleteTarget && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-slate-900 shadow-2xl">
            <h3 className="text-lg font-bold">Delete activity?</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              “{deleteTarget.title}” will be permanently removed.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={deleteActivity}
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
